import { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { studentFeeDiscountReport } from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { Link } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import Master from "./Master";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";

const num = (v) => Number(v || 0);
const inr = (n) =>
  "₹ " + num(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const MODULE_DEFS = [
  {
    key: "student_batch_n_fees",
    title: "Batch Fee",
    icon: "ri-graduation-cap-line",
    color: "primary",
    fields: {
      total: "total_fee",
      discount: "total_discount",
      final: "final_fee",
      paid: "total_paid",
      due: "total_due",
    },
  },
  {
    key: "student_hostel_n_fees",
    title: "Hostel Fee",
    icon: "ri-hotel-bed-line",
    color: "success",
    fields: {
      total: "total_amount",
      discount: null,
      final: "final_amount",
      paid: "paid_amount",
      due: "left_amount",
    },
  },
  {
    key: "student_transport_n_fees",
    title: "Transport Fee",
    icon: "ri-bus-line",
    color: "warning",
    fields: {
      total: "total_fee",
      discount: "discount",
      final: "final_fee",
      paid: "paid_fee",
      due: null,
    },
  },
  {
    key: "student_library_n_fees",
    title: "Library Fee",
    icon: "ri-book-2-line",
    color: "info",
    fields: {
      total: "total_amount",
      discount: null,
      final: "final_amount",
      paid: "paid_amount",
      due: "left_amount",
    },
  },
];

const MODULE_BADGE_BG = {
  primary: "bg-label-primary",
  success: "bg-label-success",
  warning: "bg-label-warning",
  info: "bg-label-info",
};

const getStudentModules = (item) =>
  MODULE_DEFS.map((def) => {
    const records = item?.[def.key] || [];
    if (records.length === 0) return null;

    const agg = records.reduce(
      (acc, r) => {
        const total = num(r[def.fields.total]);
        const final = num(r[def.fields.final]);
        const discount = def.fields.discount
          ? num(r[def.fields.discount])
          : total - final;
        const paid = num(r[def.fields.paid]);
        const due = def.fields.due ? num(r[def.fields.due]) : final - paid;

        acc.total += total;
        acc.discount += discount;
        acc.final += final;
        acc.paid += paid;
        acc.due += due;
        return acc;
      },
      { total: 0, discount: 0, final: 0, paid: 0, due: 0 },
    );

    return { ...def, ...agg, count: records.length };
  }).filter(Boolean);

const getStudentTotal = (modules) =>
  modules.reduce(
    (acc, m) => {
      acc.totalFee += m.total;
      acc.discount += m.discount;
      acc.finalFee += m.final;
      acc.totalPaid += m.paid;
      acc.totalDue += m.due;
      return acc;
    },
    { totalFee: 0, discount: 0, finalFee: 0, totalPaid: 0, totalDue: 0 },
  );

export default function StudentFeeDiscountReport() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({});
  const [students, setStudents] = useState([]);

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const getStudentDueFeeReport = async () => {
    setLoading(true);
    try {
      let addUrl = "";
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
      });
      const response = await studentFeeDiscountReport(addUrl);
      if (response?.data?.status) {
        setStudents(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.session_id) {
      getStudentDueFeeReport();
    }
  }, [filters]);

  const rows = useMemo(
    () =>
      students.map((item) => {
        const modules = getStudentModules(item);
        const total = getStudentTotal(modules);
        return { item, modules, total };
      }),
    [students],
  );

  const summary = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.finalFee += r.total.finalFee;
          acc.totalPaid += r.total.totalPaid;
          acc.totalDue += r.total.totalDue;
          acc.discount += r.total.discount;
          return acc;
        },
        { finalFee: 0, totalPaid: 0, totalDue: 0, discount: 0 },
      ),
    [rows],
  );

  const handleDownload = async (type = "excel") => {
    setLoading(true);
    try {
      const downloadData = rows.map(({ item, modules, total }, index) => ({
        "Registration No": item.registration_no,
        "Student Name": item.name,
        "Father Name": item.father_name,
        Phone: item.phone || "",

        "Fee Modules": modules
          .map((m) => `${m.title}(${m.total}-${m.discount}=${m.final})`)
          .join("\n\n"),
        "Payable Fee": total.finalFee,
        "Received Fee": total.totalPaid,
        "Due Fee": total.totalDue,
      }));

      if (downloadData.length) {
        if (type === "excel") {
          const now = new Date();

          const fileName = `Report_${now.getFullYear()}_${now.getMonth() + 1
            }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const feeFilters = [
    { key: "all", label: "All" },
    { key: "batch_id", label: "🎓 Batch" },
    { key: "hostel_id", label: "🏠 Hostel" },
    { key: "bus_id", label: "🚌 Transport" },
    { key: "library_id", label: "📚 Library" },
  ];

  const activeFilter =
    filters.batch_id === "only"
      ? "batch_id"
      : filters.hostel_id === "only"
        ? "hostel_id"
        : filters.bus_id === "only"
          ? "bus_id"
          : filters.library_id === "only"
            ? "library_id"
            : "all";

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    {/* ---- Header / filters card ---- */}
                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{ width: 46, height: 46 }}
                          >
                            <i className="ri-money-rupee-circle-line"></i>
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">
                              Discount Fees Report
                            </h5>
                            <small className="text-muted">
                              <i className="ri-pulse-line me-1"></i>
                              Monitor fees collection and analytics
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "220px" }}
                          >
                            <select
                              className="form-select rounded-3"
                              value={filters?.session_id || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  session_id: e.target.value,
                                })
                              }
                            >
                              {sessions?.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.session} :{" "}
                                  {s.status === "1" ? "Active" : "Inactive"}
                                </option>
                              ))}
                            </select>
                            <label>
                              <i className="ri-calendar-2-line me-1"></i>
                              Session
                            </label>
                          </div>

                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "180px" }}
                          >
                            <select
                              className="form-select rounded-3"
                              value={filters?.rte_status || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  rte_status: e.target.value,
                                })
                              }
                            >
                              <option value="">All</option>
                              <option value="RTE">RTE</option>
                              <option value="NON-RTE">NON-RTE</option>
                            </select>
                            <label>
                              <i className="ri-shield-check-line me-1"></i>
                              RTE Status
                            </label>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-danger rounded-3"
                            onClick={getStudentDueFeeReport}
                            title="Refresh"
                          >
                            <i className="ri-refresh-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ---- KPI summary cards ---- */}
                    <div className="row g-3 mb-3">
                      <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center text-white bg-secondary"
                              style={{ width: 42, height: 42 }}
                            >
                              <i className="ri-group-line fs-5"></i>
                            </div>
                            <div>
                              <small className="text-muted d-block">
                                Students
                              </small>
                              <span className="fw-bold fs-5">
                                {students?.length ?? 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center text-white bg-primary"
                              style={{ width: 42, height: 42 }}
                            >
                              <i className="ri-percent-line fs-5"></i>
                            </div>
                            <div>
                              <small className="text-muted d-block">
                                Total Discount
                              </small>
                              <span className="fw-bold fs-5">
                                {inr(summary.discount)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center text-white bg-success"
                              style={{ width: 42, height: 42 }}
                            >
                              <i className="ri-hand-coin-line fs-5"></i>
                            </div>
                            <div>
                              <small className="text-muted d-block">
                                Received
                              </small>
                              <span className="fw-bold fs-5">
                                {inr(summary.totalPaid)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center text-white bg-danger"
                              style={{ width: 42, height: 42 }}
                            >
                              <i className="ri-error-warning-line fs-5"></i>
                            </div>
                            <div>
                              <small className="text-muted d-block">
                                Total Due
                              </small>
                              <span className="fw-bold fs-5">
                                {inr(summary.totalDue)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card shadow-sm rounded-3 mb-3">
                      <div className="card-body py-3">
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          {feeFilters.map((item) => (
                            <button
                              key={item.key}
                              type="button"
                              className={`btn rounded-pill px-5 ${activeFilter === item.key
                                ? "btn-primary shadow-sm"
                                : "btn-light border"
                                }`}
                              onClick={() => {
                                if (item.key === "all") {
                                  setFilters({
                                    ...filters,
                                    batch_id: "",
                                    hostel_id: "",
                                    bus_id: "",
                                    library_id: "",
                                  });
                                  return;
                                }

                                setFilters({
                                  ...filters,
                                  batch_id: "",
                                  hostel_id: "",
                                  bus_id: "",
                                  library_id: "",
                                  [item.key]: "only",
                                });
                              }}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* ---- Report table ---- */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #ef4444, #f59e0b)",
                            }}
                          >
                            <i className="ri-file-chart-line fs-5"></i>
                          </div>
                          <div>
                            <h6 className="m-0 fw-bold">
                              Discount Fees Report
                            </h6>
                            <small className="text-muted">
                              Aggregated across batch, hostel, transport &amp;
                              library fees
                            </small>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                            <i className="ri-list-check me-1"></i>
                            {students?.length} Student
                            {students?.length === 1 ? "" : "s"}
                          </span>
                          {hasAccess("/fees/excel/list") && (
                            <button
                              type="button"
                              className="btn btn-outline-primary rounded-3"
                              onClick={() => handleDownload("excel")}
                            >
                              <i className="ri-file-excel-2-line me-2"></i>{" "}
                              Download
                            </button>
                          )}
                          {hasAccess(
                            "/fees/print/list",
                          ) && (
                              <button
                                type="button"
                                className="btn btn-outline-info rounded-3"
                                onClick={() => handleDownload("print")}
                              >
                                <i className="ri-printer-line me-2"></i> Print
                              </button>
                            )}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4 py-3 fw-semibold">#</th>
                              <th className="py-3 fw-semibold">
                                <i className="ri-user-line me-1"></i>Student
                              </th>
                              <th className="py-3 fw-semibold">
                                <i className="ri-phone-line me-1"></i>Phone
                              </th>
                              <th className="py-3 fw-semibold">
                                <i className="ri-apps-2-line me-1"></i>Fee
                                Modules
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                <i className="ri-money-rupee-circle-line me-1"></i>
                                Payable Fee
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                <i className="ri-checkbox-circle-line me-1"></i>
                                Received
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                <i className="ri-alert-line me-1"></i>Due
                              </th>
                              <th className="py-3 fw-semibold pe-4 text-end">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length > 0 ? (
                              rows.map(({ item, modules, total }, i) => (
                                <tr key={item.id}>
                                  <td className="ps-4">{i + 1}</td>
                                  <td>
                                    {hasAccess("/student/details") ? (
                                      <Link
                                        className="fw-semibold text-decoration-none"
                                        to={`/student/details?id=${item.id}`}
                                      >
                                        {item.name}{" "}
                                        <span className="text-muted fw-normal">
                                          ({item.registration_no})
                                        </span>
                                      </Link>
                                    ) : (
                                      <span className="fw-semibold">
                                        {item.name}{" "}
                                        <span className="text-muted fw-normal">
                                          ({item.registration_no})
                                        </span>
                                      </span>
                                    )}
                                    <div className="text-muted small">
                                      <i className="ri-user-follow-line me-1"></i>
                                      {item.father_name}
                                    </div>
                                  </td>
                                  <td>
                                    {item.phone ? (
                                      <a
                                        href={`tel:${item.phone}`}
                                        className="text-decoration-none text-body text-nowrap"
                                      >
                                        <i className="ri-phone-fill me-1 text-success"></i>
                                        {item.phone}
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1">
                                      {modules.length > 0 ? (
                                        modules.map((m) => (
                                          <span
                                            key={m.key}
                                            className={`badge rounded-pill ${MODULE_BADGE_BG[m.color]} d-inline-flex align-items-center gap-2 px-2 py-1`}
                                            title={`${m.title}: Total ${inr(
                                              m.total,
                                            )} · Discount ${inr(
                                              m.discount,
                                            )} · Final ${inr(
                                              m.final,
                                            )} · Due ${inr(m.due)}`}
                                          >
                                            <i className={m.icon}></i>
                                            <span
                                              className={`fw-semibold text-${m.color}`}
                                            >
                                              {m.title}
                                            </span>
                                            {m.discount > 0 && (
                                              <span className="text-muted text-decoration-line-through opacity-75">
                                                {inr(m.total)}
                                              </span>
                                            )}
                                            <span
                                              className={`fw-bold text-${m.color}`}
                                            >
                                              {inr(m.final)}
                                            </span>
                                            {m.discount > 0 && (
                                              <span className="text-success fw-semibold d-inline-flex align-items-center gap-1">
                                                <i className="ri-price-tag-3-line"></i>
                                                -{inr(m.discount)}
                                              </span>
                                            )}
                                            {m.due > 0 && (
                                              <span className="text-danger fw-bold d-inline-flex align-items-center gap-1">
                                                <i className="ri-time-line"></i>
                                                {inr(m.due)}
                                              </span>
                                            )}
                                          </span>
                                        ))
                                      ) : (
                                        <span className="text-muted small">
                                          <i className="ri-forbid-line me-1"></i>
                                          No modules
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="text-end text-nowrap">
                                    {inr(total.finalFee)}
                                  </td>
                                  <td className="text-end text-nowrap fw-semibold text-success">
                                    {inr(total.totalPaid)}
                                  </td>
                                  <td className="text-end text-nowrap fw-bold text-danger">
                                    {inr(total.totalDue)}
                                  </td>
                                  <td className="pe-4 text-end">
                                    <div className="d-flex gap-2 justify-content-end">
                                      {hasAccess("/student/details") && (
                                        <Link
                                          className="btn btn-label-primary btn-sm"
                                          to={`/student/details?id=${item.id}`}
                                          title="View student"
                                        >
                                          <i className="ri-eye-line ri-20px"></i>
                                        </Link>
                                      )}
                                      {hasAccess("/student/fees/deposit") && (
                                        <Link
                                          className="btn btn-label-danger btn-sm"
                                          to={`/student/fees/deposit?student_id=${item.id}`}
                                          title="Pay Fee"
                                        >
                                          <i className="ri-hand-coin-line"></i>
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-inbox-line fs-2 d-block mb-2 text-secondary"></i>
                                  No Due Fee Records Found
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {rows.length > 0 && (
                            <tfoot>
                              <tr style={{ background: "#f8f9fc" }}>
                                <td
                                  colSpan="4"
                                  className="ps-4 py-3 text-end fw-bold"
                                >
                                  <i className="ri-calculator-line me-1"></i>
                                  Page Total
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap">
                                  {inr(summary.finalFee)}
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap text-success">
                                  {inr(summary.totalPaid)}
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap text-danger">
                                  {inr(summary.totalDue)}
                                </td>
                                <td className="pe-4"></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
